import React, { useContext } from 'react';
import { BuilderElement } from '@builder.io/react';
import { parseDynamicProductSearchQuery, useDynamicProductSearchQuery } from '@/api/product/cms';
import { mapSearchResult } from '@/api/product/mapping';
import { DynamicProductSliderComponentProps } from '@/components/features/cms/blocks/DynamicProductSlider/register';
import { ProductSlider as DSProductSlider } from '@/components/shared';
import { useIsHighPriorityBlock } from '@/contexts/HighPriorityBlocksProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';

interface DynamicProductSliderProps extends DynamicProductSliderComponentProps {
	readonly builderBlock: BuilderElement;
}

export const DynamicProductSlider: React.FunctionComponent<DynamicProductSliderProps> = ({
	title,
	subtitle,
	products,
	count,
	hideArrows,
	builderBlock,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);

	const query = parseDynamicProductSearchQuery(segmentationId, products, count);

	const { data: results, isLoading } = useDynamicProductSearchQuery(query, settings?.data?.searchSettings);

	const mappedResults = mapSearchResult(
		results,
		query,
		translations?.data,
		undefined,
		settings?.data?.searchSettings,
	);

	const highPriority = useIsHighPriorityBlock(builderBlock.id ?? null);

	if (!isLoading && (!mappedResults || mappedResults.products.length < 1)) {
		return <p>{translations?.data.shared.noProductsFound}</p>;
	}

	return (
		<DSProductSlider
			title={title}
			subtitle={subtitle}
			products={mappedResults?.products}
			isLoading={isLoading}
			key={results?.products?.length}
			lazyLoading={!highPriority}
			showArrows={hideArrows !== true}
		/>
	);
};
